import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getEmailTemplates, updateEmailTemplate, testEmailTemplate } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Mail, Save, Loader2, Send, Eye, Code, Users,
  CheckCircle, XCircle, ChevronDown, Plus, Trash2,
} from "lucide-react";
import EmailRichEditor from "@/components/shared/EmailRichEditor";
import { TemplateUsage, SendTemplateDialog, NewTemplateDialog, useTemplateForms } from "@/components/shared/EmailTemplateCustom";

export default function AdminEmailTemplates({ embedded = false }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const forms = useTemplateForms();
  const formLabel = (k) => forms.find((f) => f.key === k)?.label || k;
  const customDesc = (t) => t.usage === "manual" ? "Sent manually" : `Auto: ${(t.trigger_forms || []).map(formLabel).join(", ") || "no form picked"}`;

  const handleDelete = async () => {
    if (!window.confirm(`Delete the "${formData.name}" template?`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates/${activeSlug}`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      toast.success("Template deleted");
      setActiveSlug(null);
      const data = await getEmailTemplates();
      setTemplates(data);
      if (data[0]) setActiveSlug(data[0].slug);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete template");
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await getEmailTemplates();
      setTemplates(data);
      if (!activeSlug && data.length > 0) {
        setActiveSlug(data[0].slug);
        setFormData(data[0]);
      }
    } catch (err) {
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const [sender, setSender] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/email-sending/sender`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } })
      .then((r) => setSender(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = templates.find((t) => t.slug === activeSlug);
    if (t) {
      setFormData(t);
      setHasChanges(false);
    }
  }, [activeSlug, templates]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // the rich editor re-emits the saved HTML on mount; only real edits count
    const saved = templates.find((t) => t.slug === activeSlug);
    if (!saved || JSON.stringify(saved[field] ?? "") !== JSON.stringify(value ?? "")) setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateEmailTemplate(activeSlug, formData);
      toast.success("Email template saved");
      setHasChanges(false);
      fetchTemplates();
    } catch (err) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await testEmailTemplate(activeSlug);
      toast.success(res.message || "Test email sent!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send test email");
    } finally {
      setTesting(false);
    }
  };

  const templateLabels = {
    dealer_application: { label: "Become a Dealer", desc: "Sent when someone submits the dealer application form" },
    contact_form: { label: "Contact Us", desc: "Sent when someone submits the contact form" },
    state_inquiry: { label: "State Page Inquiry", desc: "Sent when someone submits a Get in Touch form on a state page" },
  };

  const variableHints = {
    dealer_application: ["first_name", "last_name", "email", "phone", "company_name", "state", "city", "zip_code", "years_experience", "current_locations", "investment_capital", "message"],
    contact_form: ["first_name", "last_name", "email", "phone", "company", "message"],
    state_inquiry: ["first_name", "last_name", "email", "phone", "state", "message"],
  };

  const previewHtml = (formData.body_html || "")
    .replace(/\{\{first_name\}\}/g, "John")
    .replace(/\{\{last_name\}\}/g, "Smith")
    .replace(/\{\{email\}\}/g, "john@example.com")
    .replace(/\{\{phone\}\}/g, "(555) 123-4567")
    .replace(/\{\{company_name\}\}/g, "Smith Wireless LLC")
    .replace(/\{\{company\}\}/g, "Smith Wireless LLC")
    .replace(/\{\{state\}\}/g, "Michigan")
    .replace(/\{\{city\}\}/g, "Detroit")
    .replace(/\{\{zip_code\}\}/g, "48201")
    .replace(/\{\{years_experience\}\}/g, "5")
    .replace(/\{\{current_locations\}\}/g, "2")
    .replace(/\{\{investment_capital\}\}/g, "$100,000 - $200,000")
    .replace(/\{\{message\}\}/g, "I'm interested in learning more about becoming a dealer in my area.");

  const content = (
    <div className="space-y-6" data-testid="admin-email-templates">
      {/* Header */}
      <div className="bg-navy rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2 flex items-center gap-2">
          <Mail className="w-6 h-6" />
          Email Templates
        </h2>
        <p className="text-slate-300 font-body">
          Manage the emails your website sends. Edit the built-in auto-replies or create your own — sent automatically when a form is submitted, or sent by hand to one person.
        </p>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Template List */}
        <div className="lg:col-span-1">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-navy text-sm">Templates</CardTitle>
                <Button size="sm" variant="outline" onClick={() => setNewOpen(true)} data-testid="email-new-template-btn"><Plus className="w-3.5 h-3.5 mr-1" />New</Button>
              </div>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {templates.map((t) => {
                  const meta = templateLabels[t.slug] || { label: t.name, desc: t.is_custom ? customDesc(t) : "" };
                  return (
                    <button
                      key={t.slug}
                      onClick={() => setActiveSlug(t.slug)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                        activeSlug === t.slug ? "bg-teal text-white" : "hover:bg-slate-50"
                      }`}
                      data-testid={`email-template-tab-${t.slug}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className={`font-body text-sm font-medium ${activeSlug === t.slug ? "text-white" : "text-navy"}`}>
                          {meta.label}
                        </p>
                        <p className={`text-xs mt-0.5 truncate ${activeSlug === t.slug ? "text-white/70" : "text-slate-400"}`}>
                          {meta.desc}
                        </p>
                      </div>
                      {t.enabled ? (
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 ml-2 ${activeSlug === t.slug ? "text-white" : "text-emerald-500"}`} />
                      ) : (
                        <XCircle className={`w-4 h-4 flex-shrink-0 ml-2 ${activeSlug === t.slug ? "text-white" : "text-red-400"}`} />
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Template Editor */}
        <div className="lg:col-span-3 space-y-4">
          {loading ? (
            <Card><CardContent className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-teal" /></CardContent></Card>
          ) : !activeSlug ? (
            <Card><CardContent className="p-8 text-center text-slate-400">Select a template</CardContent></Card>
          ) : (
            <motion.div key={activeSlug} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading text-navy">
                        {templateLabels[activeSlug]?.label || formData.name}
                      </CardTitle>
                      <p className="text-sm text-slate-500 font-body mt-1">
                        {templateLabels[activeSlug]?.desc || (formData.is_custom ? customDesc(formData) : "")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasChanges && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded font-body" data-testid="email-unsaved-badge">
                          Unsaved
                        </span>
                      )}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.enabled ?? true}
                          onChange={(e) => handleChange("enabled", e.target.checked)}
                          className="rounded border-slate-300"
                          data-testid="email-enabled-toggle"
                        />
                        <span className="text-sm text-slate-600 font-body">Enabled</span>
                      </label>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-5">
                  {/* Sender Settings */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>From Name</Label>
                      <Input
                        value={formData.from_name || ""}
                        onChange={(e) => handleChange("from_name", e.target.value)}
                        placeholder="Prime Media Wireless"
                        data-testid="email-from-name"
                      />
                    </div>
                    <div>
                      <Label>Sends From</Label>
                      <Input value={sender ? (sender.connected ? sender.email : "Gmail not connected (using backup)") : ""} disabled data-testid="email-sends-from" />
                      <p className="text-xs text-slate-400 mt-1">Every template and notification goes out from the account in Settings → Email Sending.</p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>CC Recipients</Label>
                      <Input
                        value={formData.cc_emails || ""}
                        onChange={(e) => handleChange("cc_emails", e.target.value)}
                        placeholder="sam@company.com, diana@company.com"
                        data-testid="email-cc"
                      />
                      <p className="text-xs text-slate-400 mt-1">Comma-separated email addresses</p>
                    </div>
                    <div>
                      <Label>Reply-To</Label>
                      <Input
                        value={formData.reply_to || ""}
                        onChange={(e) => handleChange("reply_to", e.target.value)}
                        placeholder="info@primemediawireless.com"
                        data-testid="email-reply-to"
                      />
                    </div>
                  </div>

                  {formData.is_custom && <TemplateUsage formData={formData} forms={forms} onChange={handleChange} />}

                  {/* Subject */}
                  <div>
                    <Label>Subject Line</Label>
                    <Input
                      value={formData.subject || ""}
                      onChange={(e) => handleChange("subject", e.target.value)}
                      placeholder="Thank You for Your Interest, {{first_name}}!"
                      data-testid="email-subject"
                    />
                  </div>

                  {/* Rich Text Editor */}
                  <div>
                    <Label className="mb-2 block">Email Body</Label>
                    <EmailRichEditor
                      value={formData.body_html || ""}
                      onChange={(html) => handleChange("body_html", html)}
                      variables={variableHints[activeSlug] || ["first_name", "last_name", "email", "phone", "city", "state", "message"]}
                    />
                  </div>

                  {/* Preview Toggle */}
                  <button
                    onClick={() => setPreviewOpen(!previewOpen)}
                    className="flex items-center gap-2 text-sm font-body text-slate-600 hover:text-teal transition-colors"
                    data-testid="email-preview-toggle"
                  >
                    <Eye className="w-4 h-4" />
                    {previewOpen ? "Hide" : "Show"} Email Preview
                    <ChevronDown className={`w-4 h-4 transition-transform ${previewOpen ? "rotate-180" : ""}`} />
                  </button>

                  {previewOpen && (
                    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="email-preview">
                      <div className="bg-slate-100 px-4 py-2 text-xs text-slate-500 font-body border-b border-slate-200">
                        Preview (with sample data)
                      </div>
                      <div className="p-4 bg-white">
                        <div
                          className="email-preview-container max-w-[800px] mx-auto"
                          dangerouslySetInnerHTML={{
                            __html: `
                              <div style="font-family:Arial,Helvetica,sans-serif;max-width:640px;margin:0 auto;background:#ffffff;">
                                <div style="padding:28px 32px;text-align:center;">
                                  <img src="${process.env.REACT_APP_BACKEND_URL}/api/site-logo" alt="Prime Media Wireless" style="width:280px;height:auto;display:inline-block;" />
                                </div>
                                <hr style="border:none;border-top:1px solid #e2e8f0;margin:0;" />
                                <div style="padding:32px;">${previewHtml}</div>
                                <div style="background:#f8fafc;padding:20px 32px;text-align:center;border-top:1px solid #e2e8f0;">
                                  <p style="margin:0;color:#94a3b8;font-size:12px;">Prime Media Wireless &middot; 31455 Northwestern Hwy Ste C, Farmington Hills, MI 48334 &middot; (248) 590-0000</p>
                                </div>
                              </div>`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="pt-4 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex gap-2">
                      <Button onClick={handleSave} disabled={saving || !hasChanges} className="bg-teal hover:bg-teal-dark text-white" data-testid="email-save-btn">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save Template
                      </Button>
                      <Button onClick={handleTest} disabled={testing} variant="outline" data-testid="email-test-btn">
                        {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Send Test Email
                      </Button>
                      {formData.is_custom && (
                        <Button onClick={() => setSendOpen(true)} variant="outline" disabled={hasChanges} data-testid="email-send-btn">
                          <Send className="w-4 h-4 mr-2" />Send to someone
                        </Button>
                      )}
                      {formData.is_custom && (
                        <Button onClick={handleDelete} variant="ghost" className="text-red-600" data-testid="email-delete-btn">
                          <Trash2 className="w-4 h-4 mr-2" />Delete
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 font-body flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      CC: {formData.cc_emails || "None"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );

  const full = (
    <>
      {content}
      <NewTemplateDialog open={newOpen} forms={forms} onClose={() => setNewOpen(false)}
        onCreated={async (t) => { setNewOpen(false); setTemplates(await getEmailTemplates()); setActiveSlug(t.slug); }} />
      <SendTemplateDialog slug={activeSlug} open={sendOpen} onClose={() => setSendOpen(false)} />
    </>
  );
  if (embedded) return full;
  return full;
}
